import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LeftAuth from "../components/leftAuth";
import FormInput from "../../../shared/components/ui/form/FormInput";
import PasswordField from "../../../shared/components/ui/form/PasswordField";
import { useAuth } from "../../../shared/hooks/useAuth";
import { useFormValidation, validationRules } from "../../../shared/hooks/useFormValidation";

const Signup = () => {
  // Hook Initializations
  const navigate = useNavigate();
  const { signup, isLoading, error } = useAuth();
  const [users, setUsers] = useState([]);

  // Form Validation Hook
  const { values, errors, handleChange, handleBlur, validate } = useFormValidation(
    {
      firstName: "",
      lastName: "",
      email: "",
      organization: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
    {
      firstName: validationRules.required("First name"),
      lastName: validationRules.required("Last name"),
      email: validationRules.email,
      organization: validationRules.required("Organization"),
      password: validationRules.password,
      confirmPassword: validationRules.confirmPassword,
      agreeToTerms: (value) => (!value ? "You must agree to the terms" : ""),
    }
  );

  // Load existing users from JSON (fallback)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch("/data/signupdata.json");
        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error("Error loading signup data:", error);
      }
    };
    fetchUsers();
  }, []);

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      await signup(values);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2  grow bg-[#F7F7F8] flex justify-center items-center">
      <LeftAuth />
      <div className="h-auto lg:h-screen overflow-y-auto flex justify-center items-start py-0 lg:py-10 pb-3 lg:pb-0">
        <div className="max-w-[390px] w-full px-5">
          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <div className="text-center mb-2.5">
              <h3 className="text-4xl font-bold text-[#232526] leading-none mb-2.5">Sign Up</h3>
            </div>

            {/* first name*/}
            <FormInput label="First Name" name="firstName" value={values.firstName} onChange={handleChange} onBlur={handleBlur} error={errors.firstName} placeholder="Enter first name" required />

            {/* last name */}
            <FormInput label="Last Name" name="lastName" value={values.lastName} onChange={handleChange} onBlur={handleBlur} error={errors.lastName} placeholder="Enter last name" required />

            {/* email */}
            <FormInput label="Email Address" name="email" type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={errors.email} placeholder="Enter your email" required />

            {/* organization */}
            <FormInput label="Organization" name="organization" value={values.organization} onChange={handleChange} onBlur={handleBlur} error={errors.organization} placeholder="Enter organization name" required />

            {/* password */}
            <PasswordField label="Password" name="password" value={values.password} onChange={handleChange} onBlur={handleBlur} error={errors.password} placeholder="Enter password" required />

            {/* confirm password */}
            <PasswordField label="Confirm Password" name="confirmPassword" value={values.confirmPassword} onChange={handleChange} onBlur={handleBlur} error={errors.confirmPassword} placeholder="Confirm password" required />

            {/* agree to terms */}
            <div>
              <label className="flex items-center text-sm text-[#232526]">
                <input type="checkbox" name="agreeToTerms" checked={values.agreeToTerms} onChange={handleChange} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="ml-2">
                  I agree to the{" "}
                  <button type="button" className="text-[#4057FE] hover:underline">
                    Terms of Service
                  </button>{" "}
                  and{" "}
                  <button type="button" className="text-[#4057FE] hover:underline">
                    Privacy Policy
                  </button>
                </span>
              </label>
              {errors.agreeToTerms && <p className="text-sm text-red-500 mt-1">{errors.agreeToTerms}</p>}
            </div>

            {/* singup */}
            <button type="submit" disabled={isLoading} className="py-3 common-gradiant text-white rounded-xl font-semibold disabled:opacity-50">
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                "Create Account"
              )}
            </button>

            {/* sign in link */}
            <div className="text-center">
              <p className="text-sm text-[#232526]">
                Already have an account?{" "}
                <button type="button" onClick={() => navigate("/login")} className="text-[#4057FE] underline font-bold">
                  Sign in here
                </button>
              </p>
            </div>

            {error && <p className="text-center text-red-500 text-sm">{error}</p>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
